import type { FC, PropsWithChildren, RefObject } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useCurrentUserStore } from 'store/currentUser';
import { useTimerStore } from 'store/timer';

import { useYM } from 'YM/Context';
import { GoalType } from 'YM/declare';

interface ITimerPopupContext {
    /** Точка крепления уведомления */
    anchor: RefObject<HTMLElement> | undefined;
    /** Уведомление об оставшемся времени открыто */
    isOpen: boolean;
    /** Установка точки крепления уведомления */
    setAnchor: React.Dispatch<React.SetStateAction<RefObject<HTMLElement> | undefined>>;
}

const TimerPopupContext = createContext<ITimerPopupContext>({
    anchor: undefined,
    isOpen: false,
    setAnchor: () => undefined,
});

export const useTimerPopupContext = () => useContext(TimerPopupContext);

const TimerPopupContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { notification } = useTimerStore();
    const [anchor, setAnchor] = useState<ITimerPopupContext['anchor']>();
    const { currentUser } = useCurrentUserStore();
    const { triggerYM } = useYM();

    const contextValue = useMemo<ITimerPopupContext>(() => {
        return { anchor, isOpen, setAnchor };
    }, [isOpen, anchor]);

    useEffect(() => {
        if (isOpen) triggerYM({ goal: GoalType.CONFERENCE_PAGE_EXTEND });
    }, [isOpen]);

    useEffect(() => {
        const isManager = currentUser?.type === 'manager';

        setIsOpen(!!notification && isManager);
    }, [notification, currentUser?.type]);

    return <TimerPopupContext.Provider value={contextValue}>{children}</TimerPopupContext.Provider>;
};

export default TimerPopupContextProvider;
